/**
 * Durable sub-agent runs (ADR-0019) — the Sub-agents panel's data source.
 *
 * Distinct from ``api/subagents.ts``, which fetches ONE sub-agent's transcript
 * for the drill-in modal. This is the ledger: what ran, where, how it ended.
 */

import { apiFetch } from "@/api/client";
import type { SubagentRun } from "@/api/types";

/** Every run this conversation has had, newest first. */
export async function fetchSubagentRuns(conversationId: string): Promise<SubagentRun[]> {
  const res = await apiFetch<{ subagents: SubagentRun[] }>(
    `/conversations/${conversationId}/subagents`,
  );
  return res.subagents ?? [];
}

/**
 * Ask the environment running this sub-agent to stop it.
 *
 * Only possible while its turn is live in that backend — ``stop_task`` is a
 * method on the turn's SDK client, and there is deliberately no cross-host
 * command tunnel. The server answers 409 with a readable reason when the run is
 * already finished or is running elsewhere; callers should surface that text
 * rather than inventing their own.
 *
 * Resolves when the stop has been *requested*. The terminal row is written by
 * the CLI's own lifecycle message, so the panel updates over the WS frame a
 * moment later rather than optimistically here — one writer per outcome.
 */
export function stopSubagentRun(conversationId: string, toolUseId: string) {
  return apiFetch<{ ok: boolean; stopping: boolean }>(
    `/conversations/${conversationId}/subagents/${toolUseId}/stop`,
    { method: "POST" },
  );
}

/**
 * Close out a run whose process is already gone.
 *
 * Bookkeeping, not process control — nothing is signalled, because there is
 * nothing left to signal. This is what releases the conversation's outstanding
 * count so it can be moved between environments again.
 */
export function dismissSubagentRun(conversationId: string, toolUseId: string) {
  return apiFetch<{ ok: boolean; dismissed: boolean }>(
    `/conversations/${conversationId}/subagents/${toolUseId}/dismiss`,
    { method: "POST" },
  );
}
